import { useEffect, useRef, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import styled from "styled-components";
import Video from "twilio-video";
import { OnVideoCallParticipantChangeRequest } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { RoomGuest } from "@lifeat/shared/data-models/Rooms/RoomGuest";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { functions } from "@firebase-local/firebase";
import { ROOMS_FUNCTIONS } from "@firebase-local/functions/ServerlessEndpoints";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { RouteUtil } from "../../../../common/util";
import Widget from "../../../widget-workspace/components/Widget/Widget";
import * as RoomConstants from "../../RoomConstants";
import { RoomControls } from "../../RoomController";
import { useRoomSettingsContext } from "../../RoomSettingsContext";
import * as RoomTypes from "../../RoomTypes";
import RoomParticipant from "./RoomParticipant";
import VideoChatWidgetFooter from "./RoomVideoChatFooter";

const RoomWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    height: 100%;
    width: 100%;

    background-color: ${(props) => props.theme.backgroundPrimary};
`;

const RoomScreenShareWrapper = styled.div<{ enable: boolean }>`
    display: ${(props) => (props.enable ? "block" : "none")};
    height: 100%;
    width: 100%;

    flex-grow: 2;
`;

const RoomParticipantsWrapper = styled.div`
    display: grid;
    align-items: center;
    grid-template-rows: repeat(auto-fit, minmax(128px, 1fr));
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));

    height: 100%;
    width: 50%;
    flex-grow: 1;

    background-color: ${(props) => props.theme.backgroundPrimary};
`;

const ParticipantScreenWrapper = styled.div<{ enable: boolean }>`
    display: ${(props) => (props.enable ? "block" : "none")};
    height: 100%;
    width: 100%;
    border: 3px solid limegreen;
    background-color: black;
    &:hover {
        cursor: zoom-in;
    }
`;

const ScreenShareVideo = styled.video<{ enable: boolean }>`
    visibility: ${(props) => (props.enable ? "visible" : "hidden")};
    height: inherit;
    width: inherit;
    align-self: center;
    background-color: black;
`;

const FullScreenWrapper = styled.div<{ enable: boolean }>`
    visibility: ${(props) => (props.enable ? "visible" : "hidden")};
    display: block;
    position: fixed;
    height: 100vh;
    width: 100vw;
    left: 0;
    top: 0;
    z-index: 10;
    background: rgb(0, 0, 0, 0.8);
    padding: 20px;
    &:hover {
        cursor: zoom-out;
    }
`;

const FullScreenVideo = styled.video<{ enable: boolean }>`
    display: block;
    visibility: ${(props) => (props.enable ? "visible" : "hidden")};
    height: 100%;
    width: 100%;
    align-self: center;
    background-color: black;
    border: 3px solid limegreen;
`;

const INIT_ROOM_PARTICIPANTS_VOL = 0.8;

// This positioning puts the participants view close to the room controls on app load.
const PARTICIPANTS_WIDGET_INITIAL_POS_X = isMobile && !isTablet ? 10 : 380;
const PARTICIPANTS_WIDGET_INITIAL_POS_Y = 50;

const FREE_CALL_DURATION_MINS = 15;
const FREE_CALL_DURATION_SECONDS = RouteUtil.isProductionRoute() ? FREE_CALL_DURATION_MINS * 60 : 60; // 1 minute if on test environment
const CALL_WILL_END_THRESHOLD_SECONDS = 59;

interface RoomVideoChatProps {
    roomId: string;
    roomController: RoomControls;
    /**
     * Data about all guests. Keyed by guest's email address (unique).
     */
    guestsData: { [id: string]: RoomGuest };
    showParticipantsView: boolean;
    setShowParticipantsView: (show: boolean) => void;
}
export default function RoomVideoChat(props: RoomVideoChatProps) {
    const { roomId, roomController } = props;
    const { isVideoCallingDisabled, isScreenShareDisabled } = useRoomSettingsContext();
    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();
    const { user, userData }: UserContextType = useUserContext();

    const [isTrialStarted, setIsTrialStarted] = useState(false);
    const [trialTimeLeftSec, setTrialTimeLeftSec] = useState(FREE_CALL_DURATION_SECONDS);

    useEffect(() => {
        if (
            userData &&
            !userData.proUserInfo?.isProUser &&
            !isTrialStarted &&
            roomController.isVideoChatInit &&
            roomController.roomInfo
        ) {
            // Only start the trial countdown if the Room host does not have Pro.
            // Pro users have access to unlimited video chat and non-Pro users can join as
            // guests, and it will be indefinite usage.
            if (!roomController.roomInfo.isRoomHostPro && roomId === userData?.roomsData?.personalRoomId) {
                setIsTrialStarted(true);
                const endCallTimeout = setTimeout(() => {
                    roomController.leaveRoomCall();
                    setUpgradeCtaContext("unlimitedVideoChat", UpgradeCTASource.ROOMS);
                }, FREE_CALL_DURATION_SECONDS * 1000);

                const secondsCountdown = setInterval(() => {
                    setTrialTimeLeftSec((oldTrialTimeLeftSec) => {
                        return oldTrialTimeLeftSec - 1;
                    });
                }, 1000);

                return () => {
                    clearTimeout(endCallTimeout);
                    clearInterval(secondsCountdown);
                };
            }
        }
        return undefined;
    }, [userData, roomController.isVideoChatInit]);

    const [room, setRoom] = useState<Video.Room | null>(null);

    const screenShareRef = useRef(null);
    const fullScreenRef = useRef(null);
    const [focusScreen, setFocusScreen] = useState(false);
    useEffect(() => {
        if (!roomController.isRoomScreenSharing) {
            setFocusScreen(false);
        }
    }, [roomController.isRoomScreenSharing]);

    const [participants, setParticipants] = useState<Video.Participant[]>([]);
    useEffect(() => {
        if (participants) {
            roomController.setNumCallParticipants(participants.length + 1); // add 1 to include host
        }
    }, [participants]);

    // The volume of all other participants. Acts as volume control for the video chat call,
    // so that the volume can be set relative to other audio sources in the app (e.g.
    // space audio, soundboard, timer, etc.)
    const [participantsVolume, setParticipantsVolume] = useState<number>(INIT_ROOM_PARTICIPANTS_VOL);

    // Handles participant connecting/disconnecting from the Video call room.
    useEffect(() => {
        if (roomController.token && roomController.isVideoChatInit) {
            const participantConnected = (participant: Video.RemoteParticipant) => {
                setParticipants((prevParticipants) => [...prevParticipants, participant]);
            };
            const participantDisconnected = (participant: Video.RemoteParticipant) => {
                setParticipants((prevParticipants) => prevParticipants.filter((p) => p !== participant));
            };
            const handleStatusChange = async () => {
                const payload: OnVideoCallParticipantChangeRequest = { roomId };
                await functions.httpsCallable(ROOMS_FUNCTIONS.OnVideoCallParticipantChange)(payload);
            };

            // Connect to the video call
            Video.connect(roomController.token, {
                name: roomId,
                video: { name: RoomConstants.VideoChatTrackNames.USER_WEBCAM },
                audio: { name: RoomConstants.VideoChatTrackNames.USER_MIC },
                preferredVideoCodecs: "auto",
                bandwidthProfile: {
                    video: {
                        clientTrackSwitchOffControl: "auto",
                        contentPreferencesMode: "auto",
                    },
                },
            })
                .then((room: Video.Room) => {
                    setRoom(room);
                    room.on("participantConnected", participantConnected);
                    room.on("participantDisconnected", participantDisconnected);
                    room.on("disconnected", handleStatusChange);
                    room.participants.forEach(participantConnected);
                })
                .then(handleStatusChange)
                .catch((error: Error) => {
                    // Leave the Room call, so it doesn't seem like there is a call running to the user
                    // before alerting them of the problem
                    roomController.leaveRoomCall();
                    alert(
                        `There was an error with the video call.\n\nError: ${error?.name}\n\nReason: ${error?.message}` +
                            `\n\nYou may be able to resolve this error yourself, or you can request ` +
                            `support by emailing contact@lifeat.io (or messaging in Discord) with this ` +
                            `error message. Thank you!`,
                    );
                });
        }
        // NOTE: clean up for disconnecting the room is handled outside of this useEffect
    }, [roomController.isVideoChatInit, roomController.token]);

    // NOTE: this useEffect relates to the above, and is necessary to disconnect
    //    from the video call when the user leaves the Room.
    //  When the cleanup is used in the above useEffect, it does not actually disconnect,
    //    because 'room' is null at the time and then the useEffect does not run again.
    useEffect(() => {
        return () => {
            if (room && room.localParticipant.state === "connected") {
                roomController.leaveRoomCall();
                // @ts-ignore - Removing type shows that stop() function does not exist on track
                room.localParticipant.tracks.forEach((trackPublication: RoomTypes.LocalTrackPublicationType) => {
                    trackPublication.track.stop();
                });
                room.disconnect();
            }
        };
    }, [room]);

    const handleScreenShare = (isSharing: boolean, screenTrack: RoomTypes.TrackType) => {
        if (!isScreenShareDisabled) {
            if (isSharing) {
                screenTrack.attach(screenShareRef.current ?? undefined);
                screenTrack.attach(fullScreenRef.current ?? undefined);
                roomController.setIsRoomScreenSharing(true);
            } else {
                screenTrack.detach();
                roomController.setIsRoomScreenSharing(false);
            }
        }
    };

    const screenShareView = !isScreenShareDisabled && (
        <ParticipantScreenWrapper
            enable={roomController.isRoomScreenSharing && !focusScreen}
            onClick={() => setFocusScreen(true)}
        >
            <ScreenShareVideo
                ref={screenShareRef}
                autoPlay={true}
                muted={false}
                enable={roomController.isRoomScreenSharing}
            />
        </ParticipantScreenWrapper>
    );

    const fullScreenView = (
        <FullScreenWrapper className={"full-screen-wrap"} onClick={() => setFocusScreen(false)} enable={focusScreen}>
            <FullScreenVideo
                ref={fullScreenRef}
                autoPlay={true}
                muted={false}
                enable={roomController.isRoomScreenSharing && focusScreen}
            />
        </FullScreenWrapper>
    );

    const remoteParticipants = participants.map((participant, idx) => (
        <RoomParticipant
            key={participant.sid}
            participant={participant}
            participantDisplayName={props.guestsData?.[participant.identity]?.userDisplayName ?? "User"}
            participantNumber={idx + 1}
            isLocalClient={false}
            volume={participantsVolume}
            canShareScreen={!roomController.isRoomScreenSharing}
            handleShareScreen={handleScreenShare}
        />
    ));

    const vidChatHeader = <VideoChatWidgetHeader numSecondsLeft={trialTimeLeftSec} />;

    const vidChatFooter = props.roomController.isVideoChatInit ? (
        <VideoChatWidgetFooter
            roomVolume={participantsVolume}
            setRoomVolume={(newVol: number) => setParticipantsVolume(newVol)}
            roomController={props.roomController}
        />
    ) : undefined;

    return isVideoCallingDisabled ? null : (
        <>
            <Widget
                name={"VidChat"}
                amplitudeEventSource="focus-mode"
                isUserAuthenticated={true}
                headerContent={vidChatHeader}
                footerContent={vidChatFooter}
                showTitle={true}
                isOpen={props.showParticipantsView}
                isStatic={false}
                isResizable={true}
                initialPos={{
                    x: PARTICIPANTS_WIDGET_INITIAL_POS_X,
                    y: PARTICIPANTS_WIDGET_INITIAL_POS_Y,
                }}
                lightMode={lightMode}
                savedLayout={{
                    // this is hacked around the purpose of this implementation
                    wasOpen: true,
                    xPos: PARTICIPANTS_WIDGET_INITIAL_POS_X,
                    yPos: PARTICIPANTS_WIDGET_INITIAL_POS_Y,
                    height: 360,
                    width: 360,
                }}
                footerAutoHide={false}
            >
                <RoomWrapper className={`room-video-chat-wrapper`}>
                    <RoomScreenShareWrapper enable={roomController.isRoomScreenSharing}>
                        {screenShareView}
                    </RoomScreenShareWrapper>
                    <RoomParticipantsWrapper>
                        {room && (
                            <RoomParticipant
                                key={room.localParticipant.sid}
                                participant={room.localParticipant}
                                participantDisplayName={userData?.displayName ?? user?.displayName ?? "User"}
                                participantNumber={0}
                                isLocalClient={true}
                                localPart={room.localParticipant}
                                canShareScreen={!roomController.isRoomScreenSharing}
                                handleShareScreen={handleScreenShare}
                            />
                        )}

                        {remoteParticipants}
                    </RoomParticipantsWrapper>
                </RoomWrapper>
            </Widget>

            {/* Note: this view is overlaid on top of the whole app */}
            {fullScreenView}
        </>
    );
}

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const TimerWrapper = styled.div``;

interface VideoChatWidgetHeaderProps {
    readonly numSecondsLeft?: number;
}

function VideoChatWidgetHeader(props: VideoChatWidgetHeaderProps) {
    return (
        <HeaderWrapper>
            {/* Show countdown if call is about to end. */}
            {props.numSecondsLeft && props.numSecondsLeft <= CALL_WILL_END_THRESHOLD_SECONDS && (
                <TimerWrapper>(Call ending in {props.numSecondsLeft} seconds)</TimerWrapper>
            )}
        </HeaderWrapper>
    );
}
